/* ============================================================
   MARKETPLACE  —  SESAAT Studio v3.9
   ============================================================
   Fail ini berkhidmat kepada DUA laluan, dikawal oleh `modSebenar`:

     modSebenar = false  →  /marketplace-v3.9  (PELAN reka bentuk)
       Produk contoh tetap. Kad tidak boleh diklik. Ada suis demo
       Biasa/Memuat/Kosong untuk menilai setiap keadaan skrin.

     modSebenar = true   →  /marketplace  (HALAMAN HIDUP)
       Produk sebenar daripada Firestore, gambar sebenar, kad
       membuka halaman produk. Suis demo dan label pelan DIBUANG —
       kedua-duanya alat menilai rupa, bukan untuk pengguna.

   ⛔ MOD PELAN TIDAK BOLEH BERUBAH. Corak sama seperti StudioRail:
      setiap tambahan untuk app sebenar dipagar `modSebenar`, supaya
      /marketplace-v3.9 kekal betul-betul seperti sebelum ini.

   Rupa kad hanya ada SATU salinan. Kedua-dua sumber data ditukar
   kepada bentuk yang sama oleh `dariContoh`/`dariFirestore` di bawah
   — jadi bila rupa kad diubah, kedua-dua mod ikut serentak dan tiada
   versi yang tertinggal.

   ⚠️ Harga daripada Firestore disimpan dalam SEN (integer). Gunakan
      `formatPrice` — jangan sekali-kali papar nilai mentah.

   Elemen yang dikekalkan daripada halaman lama (MarketplaceView.jsx):
      · penapis kategori (Semua · Ebook · Akaun TikTok · Flow Ultra)
      · butang "Pembelian Saya"
      · kad: gambar 4:3, pil kategori, harga + harga asal dicoret,
        reben PERCUMA, reben diskaun, lapisan SOLD OUT,
        amaran stok tinggal sedikit
      · keadaan memuat (skeleton), keadaan kosong, mesej gagal muat
   ============================================================ */

import { useEffect, useMemo, useState } from 'react';
import { Receipt, Flame } from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, PageHeader, Segmented, Card, EmptyState } from '../design/ui';
import { IKON_KATEGORI, IKON_LAIN } from './marketplaceIcons';
import { listPublishedProducts, categoryLabel, formatPrice, CATEGORIES } from '../services/marketplaceService';
import { toast } from './toastBus';
import { ButangTroli } from './TroliV39View';
import { useT } from '../i18n';

/* Pemalar aras-modul tidak boleh memanggil useT(), jadi senarai ini
   menyimpan KUNCI kamus; ia diterjemah pada masa render di bawah. */
const KATEGORI_PELAN = [
    { value: 'all', kunci: 'products.market.cat.all' },
    { value: 'ebook', kunci: 'products.market.cat.ebook' },
    { value: 'tiktok_account', kunci: 'products.market.cat.tiktok' },
    { value: 'flow_ultra', kunci: 'products.market.cat.flowUltra' },
];

/* Mod sebenar membaca senarai kategori daripada daftar sistem, jadi
   kategori baharu yang ditambah kemudian muncul di sini dengan
   sendirinya — tiada tempat kedua untuk dikemas kini. (Label kategori
   itu milik marketplaceService dan hanya ada versi Melayu — jadi
   kategori yang ADA dalam KATEGORI_PELAN diterjemah ikut kuncinya
   (2026-09-23: "Akaun TikTok" dahulu kekal Melayu dalam mod EN);
   kategori baharu yang belum masuk kamus jatuh ke label servis.) */
const KUNCI_KATEGORI = Object.fromEntries(KATEGORI_PELAN.map((c) => [c.value, c.kunci]));
const KATEGORI_SEBENAR = CATEGORIES.map((c) => ({ value: c.id, label: c.label, kunci: KUNCI_KATEGORI[c.id] }));
const labelKategori = (t, id) => (KUNCI_KATEGORI[id] ? t(KUNCI_KATEGORI[id]) : categoryLabel(id));

const PRODUK = [
    { id: 'm1', kategori: 'tiktok_account', tajuk: 'Akaun TikTok Aged 2023 — 12K Followers, Niche Kecantikan', harga: 450, asal: 650, stok: 3 },
    { id: 'm2', kategori: 'flow_ultra', tajuk: 'Flow Ultra — Akaun Google Labs Sedia Guna', harga: 199, asal: 0, stok: 12 },
    { id: 'm3', kategori: 'ebook', tajuk: 'Panduan Lengkap TikTok Shop Affiliate 2026', harga: 0, asal: 0, stok: 999, percuma: true },
    { id: 'm4', kategori: 'tiktok_account', tajuk: 'Akaun TikTok Fresh — Verified Email, Sedia Bind MCN', harga: 120, asal: 0, stok: 0 },
    { id: 'm5', kategori: 'ebook', tajuk: 'Skrip Hook Viral — 200 Contoh Ayat Pembuka Yang Terbukti Menjual Untuk Produk Kecantikan Dan Kesihatan', harga: 39, asal: 79, stok: 999 },
    { id: 'm6', kategori: 'flow_ultra', tajuk: 'Flow Ultra Bundle — 3 Akaun', harga: 549, asal: 597, stok: 2 },
];

const wang = (n) => `RM${n.toFixed(2)}`;
const peratusDiskaun = (harga, asal) => (!asal || asal <= harga ? 0 : Math.round((1 - harga / asal) * 100));

/** Produk contoh (ringgit penuh) → bentuk kad. */
function dariContoh(p) {
    return {
        id: p.id,
        kategori: p.kategori,
        tajuk: p.tajuk,
        imej: '',
        harga: p.percuma ? 'RM0.00' : wang(p.harga),
        asal: !p.percuma && p.asal > p.harga ? wang(p.asal) : '',
        stok: p.stok,
        percuma: !!p.percuma,
        habis: p.stok === 0,
        disc: peratusDiskaun(p.harga, p.asal),
    };
}

/** Produk Firestore (harga dalam SEN) → bentuk kad. */
function dariFirestore(p) {
    const percuma = p.pricingMode === 'free';
    const habis = p.status === 'sold_out' || p.stock === 0;
    return {
        id: p.id,
        kategori: p.category,
        tajuk: p.title,
        imej: p.imageUrl || '',
        harga: percuma ? 'RM0.00' : formatPrice(p.price),
        asal: !percuma && p.originalPrice > p.price ? formatPrice(p.originalPrice) : '',
        stok: typeof p.stock === 'number' ? p.stock : null,
        percuma,
        habis,
        disc: percuma ? 0 : peratusDiskaun(p.price, p.originalPrice),
    };
}

function KadProduk({ p, onBuka }) {
    const t = useT();
    const Ikon = IKON_KATEGORI[p.kategori] || IKON_LAIN;
    const bolehKlik = typeof onBuka === 'function';

    /* Kad habis stok KEKAL boleh diklik — sama seperti halaman lama.
       Pembeli masih patut boleh membuka dan membaca butiran produk
       yang sudah habis. Hanya kesan hover yang dimatikan. */
    const sifatKlik = bolehKlik
        ? {
            onClick: () => onBuka(p.id),
            onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onBuka(p.id); } },
            role: 'button',
            tabIndex: 0,
        }
        : {};

    return (
        <Card
            hover={!p.habis}
            {...sifatKlik}
            style={{
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
                opacity: p.habis ? 0.6 : 1, cursor: bolehKlik ? 'pointer' : 'default',
            }}
        >
            <div style={{
                aspectRatio: '4 / 3', position: 'relative',
                display: 'grid', placeItems: 'center', borderBottom: '1px solid var(--s-line)',
                background: p.imej ? `var(--s-ground) center/cover no-repeat url(${JSON.stringify(p.imej)})` : 'var(--s-ground)',
            }}>
                {!p.imej && <Ikon size={30} strokeWidth={1.4} style={{ color: 'var(--s-ink-3)' }} />}

                <span style={{
                    position: 'absolute', bottom: 8, left: 8,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'var(--s-card-foot)', border: '1px solid var(--s-line)',
                    color: 'var(--s-ink-2)', fontSize: 11, padding: '3px 8px', borderRadius: 999,
                }}>
                    <Ikon size={11} strokeWidth={2} />{labelKategori(t, p.kategori)}
                </span>

                {/* Satu reben sahaja pada satu masa — kalau kedua-duanya
                    dibenarkan muncul serentak, sudut kad jadi sesak. */}
                {!p.habis && p.percuma && (
                    <span style={{ position: 'absolute', top: 8, right: 8 }}><Badge tone="ok">{t('products.market.free')}</Badge></span>
                )}
                {!p.habis && !p.percuma && p.disc > 0 && (
                    <span style={{ position: 'absolute', top: 8, right: 8 }}><Badge tone="stop">-{p.disc}%</Badge></span>
                )}

                {p.habis && (
                    <div style={{
                        position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
                        background: 'rgba(17,17,17,.62)',
                    }}>
                        <span style={{
                            fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em',
                            color: 'var(--s-ink)', border: '1px solid var(--s-line-strong)',
                            padding: '5px 14px', borderRadius: 999,
                        }}>{t('products.market.soldOut')}</span>
                    </div>
                )}
            </div>

            <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* Dua baris tetap — tajuk marketplace panjang berbeza-beza;
                    tanpa had ini tinggi kad dalam grid jadi tak sekata. */}
                <div style={{
                    fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', lineHeight: 1.35,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', minHeight: 38, marginBottom: 8,
                }}>
                    {p.tajuk}
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 'auto' }}>
                    <span style={{
                        fontSize: 17, fontWeight: 600,
                        color: p.percuma ? 'var(--s-ok)' : 'var(--s-ink)',
                        fontVariantNumeric: 'tabular-nums',
                    }}>
                        {p.harga}
                    </span>
                    {p.asal && (
                        <span className="s-dim" style={{ textDecoration: 'line-through' }}>{p.asal}</span>
                    )}
                </div>

                {!p.habis && typeof p.stok === 'number' && p.stok <= 5 && (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 9,
                        fontSize: 11.5, color: 'var(--s-warn)',
                    }}>
                        <Flame size={12} strokeWidth={2} />{t('products.market.stockLeft', { n: p.stok })}
                    </span>
                )}
            </div>
        </Card>
    );
}

export default function MarketplaceV39View({ onNavigate, modSebenar = false }) {
    const t = useT();
    const [kat, setKat] = useState('all');
    const [keadaan, setKeadaan] = useState('biasa');   // mod pelan sahaja
    const [senarai, setSenarai] = useState([]);        // mod sebenar sahaja
    const [memuatData, setMemuatData] = useState(modSebenar);

    useEffect(() => {
        if (!modSebenar) return;
        let hidup = true;
        (async () => {
            try {
                const hasil = await listPublishedProducts();
                if (hidup) setSenarai(hasil);
            } catch (e) {
                toast.error(t('products.market.loadFailed', { ralat: e?.message || e }));
            } finally {
                if (hidup) setMemuatData(false);
            }
        })();
        return () => { hidup = false; };
        /* `t` sengaja TIDAK dimasukkan dalam senarai kebergantungan —
           ia bertukar apabila bahasa bertukar, dan menambahnya di sini
           akan mencetuskan bacaan Firestore sekali lagi. */
    }, [modSebenar]); // eslint-disable-line react-hooks/exhaustive-deps

    const semua = useMemo(
        () => (modSebenar ? senarai.map(dariFirestore) : PRODUK.map(dariContoh)),
        [modSebenar, senarai],
    );

    const ditapis = useMemo(
        () => (kat === 'all' ? semua : semua.filter((p) => p.kategori === kat)),
        [kat, semua],
    );

    const memuat = modSebenar ? memuatData : keadaan === 'memuat';
    const kosong = modSebenar ? ditapis.length === 0 : (keadaan === 'kosong' || ditapis.length === 0);

    /* Label penapis diterjemah di sini, bukan dalam pemalar aras-modul. */
    const pilihanKategori = modSebenar
        ? [{ value: 'all', label: t('products.market.cat.all') },
            ...KATEGORI_SEBENAR.map((c) => ({ value: c.value, label: c.kunci ? t(c.kunci) : c.label }))]
        : KATEGORI_PELAN.map((c) => ({ value: c.value, label: t(c.kunci) }));

    const isi = (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>

            <PageHeader
                title={t('products.market.title')}
                subtitle={t('products.market.subtitle')}
                actions={
                    <>
                        {!modSebenar && (
                            <Segmented
                                value={keadaan}
                                onChange={setKeadaan}
                                options={[
                                    { value: 'biasa', label: t('products.market.demo.normal') },
                                    { value: 'memuat', label: t('products.market.demo.loading') },
                                    { value: 'kosong', label: t('products.market.demo.empty') },
                                ]}
                            />
                        )}
                        {/* Mod PELAN tidak diberi troli — halaman itu alat menilai
                            rupa, dan trolinya akan berkongsi simpanan sebenar
                            pelanggan. Lihat peraturan mod pelan di kepala fail. */}
                        {modSebenar && <ButangTroli onNavigate={onNavigate} />}
                        <Btn variant="ghost" icon={<Receipt size={15} strokeWidth={2} />}
                            onClick={() => onNavigate?.('/marketplace/pembelian')}>
                            {t('products.market.myPurchases')}
                        </Btn>
                    </>
                }
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
                <Segmented value={kat} onChange={setKat} options={pilihanKategori} />
                {!modSebenar && <span className="s-dim" style={{ marginLeft: 'auto' }}>{t('products.market.planNote')}</span>}
            </div>

            {memuat ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="s-skel" style={{
                            borderRadius: 'var(--s-r-lg)', border: '1px solid var(--s-line)',
                            background: 'var(--s-card)', aspectRatio: '4 / 5.2',
                        }} />
                    ))}
                </div>
            ) : kosong ? (
                <EmptyState title={t('products.market.emptyTitle')}>
                    {t('products.market.emptyDesc')}
                </EmptyState>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 }}>
                    {ditapis.map((p) => (
                        <KadProduk
                            key={p.id}
                            p={p}
                            onBuka={modSebenar ? (id) => onNavigate?.(`/marketplace/p/${id}`) : undefined}
                        />
                    ))}
                </div>
            )}

        </div>
    );

    /* Mod pelan sudah dibalut ContentPanel oleh StudioShell — kalau
       dibalut sekali lagi di sini, akan jadi dua panel bersarang.

       Mod sebenar tidak melalui StudioShell (rel menu sahaja yang
       ditukar dalam App.jsx), jadi halaman ini yang memasang panelnya
       sendiri — bekas yang SAMA, supaya rupanya betul-betul seperti
       prototaip. `berlatar` wajib: <main> app lama berlatar terang,
       dan tanpa ini jurang 10px keliling panel jadi jalur putih. */
    /* Bingkai dipasang SEKALI di App.jsx (`BingkaiHalaman`) untuk seluruh
       app; mod pelan pula dibalut StudioShell. Halaman ini tidak memasang
       bingkainya sendiri (tukar 2026-08-11) — dua lapis = kotak dalam kotak. */
    return isi;
}
